import { getAllUsersForNames, getEmployerById } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Turning the author IDs on notes and history entries into names.
 *
 * `changedBy` stores an ID, not a label, so anything rendering it directly
 * shows a 24-character ObjectId where a person's name belongs. Two lookups
 * cover it: the internal users list resolves admins and HRs in one request,
 * and an employer who added a shared note is fetched by ID on demand.
 *
 * Both failures are non-fatal — an unresolved ID falls back to itself, which
 * is worse to read but still identifies who acted.
 */

const nameOf = (pd: any): string =>
  pd?.fullName || `${pd?.firstName ?? ''} ${pd?.lastName ?? ''}`.trim();

export function useAuthorNames(ids: Array<string | undefined>) {
  const [names, setNames] = useState<Record<string, string>>({});
  /** Every ID already looked up, resolved or not — stops a refetch loop. */
  const attempted = useRef<Set<string>>(new Set());

  // The internal users list, once. Covers admins and HRs, who write most notes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchGraphQLApiData(getAllUsersForNames);
        const map: Record<string, string> = {};
        (data?.getAllUsers?.content ?? []).forEach((u: any) => {
          const name = nameOf(u?.personalDetails);
          if (u?._id && name) {
            map[u._id] = name;
            attempted.current.add(u._id);
          }
        });
        // Existing entries win: an on-demand lookup that already landed is
        // never overwritten by this slower bulk one.
        if (!cancelled && Object.keys(map).length) {
          setNames((prev) => ({ ...map, ...prev }));
        }
      } catch {
        // Leaves the raw ID showing, which still identifies the actor.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Whatever the users list did not cover — an employer on a shared note.
  const key = ids.filter(Boolean).join(',');
  useEffect(() => {
    const unknown = [...new Set(ids.filter(Boolean) as string[])]
      .filter((id) => !names[id] && !attempted.current.has(id));
    if (unknown.length === 0) return;
    unknown.forEach((id) => attempted.current.add(id));

    let cancelled = false;
    (async () => {
      const found: Record<string, string> = {};
      await Promise.all(unknown.map(async (id) => {
        try {
          const { data } = await fetchGraphQLApiData(getEmployerById(id));
          const name = nameOf(data?.getEmployerById?.personalDetails);
          if (name) found[id] = name;
        } catch {
          // ignore — the raw ID stays
        }
      }));
      if (!cancelled && Object.keys(found).length) {
        setNames((prev) => ({ ...prev, ...found }));
      }
    })();
    return () => { cancelled = true; };
    // Keyed on the joined IDs, not the array: a new array of the same IDs
    // renders every time the record does, and would refire this on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return useCallback(
    (id?: string) => (id && names[id]) || id || 'Unknown',
    [names],
  );
}
